const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api"

// Helper function to get auth headers
function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem("admin_token")
    return {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Token ${token}` } : {}),
    }
}

// Helper function for multipart form data (file uploads)
function getAuthHeadersMultipart(): HeadersInit {
    const token = localStorage.getItem("admin_token")
    return {
        Accept: "application/json",
        ...(token ? { Authorization: `Token ${token}` } : {}),
    }
}

// Types
export interface DashboardStats {
    products_count: number
    categories_count: number
    orders_count: number
    pending_orders: number
    confirmed_orders: number
    delivered_orders: number
    total_revenue: number
    recent_orders: Order[]
    low_stock_products: Product[]
}

export interface Product {
    id: number
    name: string
    slug: string
    description: string
    price: string
    image: string | null
    image_url: string | null
    images: ProductImage[]
    all_image_urls: string[]
    stock: number
    available: boolean
    category: number
    category_name: string
    created_at: string
    updated_at: string
}

export interface ProductImage {
    id: number
    image: string
    image_url: string
    is_primary: boolean
    order: number
}

export interface Category {
    id: number
    name: string
    slug: string
    description: string
    image: string | null
    image_url: string | null
    products_count: number
    created_at: string
}

export interface Order {
    id: number
    product: number
    product_name: string
    product_image: string | null
    customer_name: string
    phone_number: string
    quantity: number
    total_price: string
    status: "pending" | "confirmed" | "delivered" | "cancelled"
    note: string
    created_at: string
    updated_at?: string
}

export interface HeroSlide {
    id: number
    title: string
    subtitle: string
    image: string | null
    image_url: string | null
    button_text: string
    button_link: string
    order: number
    active: boolean
}

export interface Translation {
    id: number
    page: string
    key: string
    text_uz: string
    text_en: string
    text_ru: string
    description: string
}

export interface SiteSettings {
    site_name: string
    site_description: string
    logo: string | null
    logo_url: string | null
    email: string
    phone: string
    address: string
    instagram: string
    telegram: string
    facebook: string
    location_url: string
}

export interface TelegramSettings {
    id?: number
    bot_token: string
    channel_id: string
    is_active: boolean
}

// API Functions
export const adminApi = {
    // Dashboard
    getDashboardStats: async (): Promise<DashboardStats> => {
        const response = await fetch(`${API_BASE_URL}/admin/dashboard/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch dashboard stats")
        return response.json()
    },

    // Products
    getProducts: async (): Promise<Product[]> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch products")
        const data = await response.json()
        return Array.isArray(data) ? data : data.results || []
    },

    getProduct: async (id: number): Promise<Product> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/${id}/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch product")
        return response.json()
    },

    createProduct: async (formData: FormData): Promise<Product> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/`, {
            method: "POST",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || "Failed to create product")
        }
        return response.json()
    },

    updateProduct: async (id: number, formData: FormData): Promise<Product> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/${id}/`, {
            method: "PUT",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || "Failed to update product")
        }
        return response.json()
    },

    deleteProduct: async (id: number): Promise<void> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/${id}/`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to delete product")
    },

    addProductImages: async (productId: number, formData: FormData): Promise<ProductImage[]> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images/`, {
            method: "POST",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) throw new Error("Failed to add images")
        return response.json()
    },

    deleteProductImage: async (productId: number, imageId: number): Promise<void> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images/${imageId}/`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to delete image")
    },

    setPrimaryImage: async (productId: number, imageId: number): Promise<void> => {
        const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images/${imageId}/primary/`, {
            method: "POST",
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to set primary image")
    },

    // Categories
    getCategories: async (): Promise<Category[]> => {
        const response = await fetch(`${API_BASE_URL}/admin/categories/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch categories")
        const data = await response.json()
        return Array.isArray(data) ? data : data.results || []
    },

    getCategory: async (id: number): Promise<Category> => {
        const response = await fetch(`${API_BASE_URL}/admin/categories/${id}/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch category")
        return response.json()
    },

    createCategory: async (formData: FormData): Promise<Category> => {
        const response = await fetch(`${API_BASE_URL}/admin/categories/`, {
            method: "POST",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || "Failed to create category")
        }
        return response.json()
    },

    updateCategory: async (id: number, formData: FormData): Promise<Category> => {
        const response = await fetch(`${API_BASE_URL}/admin/categories/${id}/`, {
            method: "PUT",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || "Failed to update category")
        }
        return response.json()
    },

    deleteCategory: async (id: number): Promise<void> => {
        const response = await fetch(`${API_BASE_URL}/admin/categories/${id}/`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to delete category")
    },

    // Orders
    getOrders: async (): Promise<Order[]> => {
        const response = await fetch(`${API_BASE_URL}/admin/orders/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch orders")
        const data = await response.json()
        return Array.isArray(data) ? data : data.results || []
    },

    getOrder: async (id: number): Promise<Order> => {
        const response = await fetch(`${API_BASE_URL}/admin/orders/${id}/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch order")
        return response.json()
    },

    updateOrderStatus: async (id: number, status: Order["status"]): Promise<Order> => {
        const response = await fetch(`${API_BASE_URL}/admin/orders/${id}/`, {
            method: "PATCH",
            headers: getAuthHeaders(),
            body: JSON.stringify({ status }),
        })
        if (!response.ok) throw new Error("Failed to update order status")
        return response.json()
    },

    // Hero Slides
    getHeroSlides: async (): Promise<HeroSlide[]> => {
        const response = await fetch(`${API_BASE_URL}/admin/hero-slides/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch hero slides")
        const data = await response.json()
        return Array.isArray(data) ? data : data.results || []
    },

    createHeroSlide: async (formData: FormData): Promise<HeroSlide> => {
        const response = await fetch(`${API_BASE_URL}/admin/hero-slides/`, {
            method: "POST",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) throw new Error("Failed to create hero slide")
        return response.json()
    },

    updateHeroSlide: async (id: number, formData: FormData): Promise<HeroSlide> => {
        const response = await fetch(`${API_BASE_URL}/admin/hero-slides/${id}/`, {
            method: "PUT",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) throw new Error("Failed to update hero slide")
        return response.json()
    },

    deleteHeroSlide: async (id: number): Promise<void> => {
        const response = await fetch(`${API_BASE_URL}/admin/hero-slides/${id}/`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to delete hero slide")
    },

    // Translations
    getTranslations: async (): Promise<Translation[]> => {
        const response = await fetch(`${API_BASE_URL}/admin/translations/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch translations")
        const data = await response.json()
        return Array.isArray(data) ? data : data.results || []
    },

    createTranslation: async (data: Omit<Translation, "id">): Promise<Translation> => {
        const response = await fetch(`${API_BASE_URL}/admin/translations/`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        if (!response.ok) throw new Error("Failed to create translation")
        return response.json()
    },

    updateTranslation: async (id: number, data: Partial<Translation>): Promise<Translation> => {
        const response = await fetch(`${API_BASE_URL}/admin/translations/${id}/`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        if (!response.ok) throw new Error("Failed to update translation")
        return response.json()
    },

    deleteTranslation: async (id: number): Promise<void> => {
        const response = await fetch(`${API_BASE_URL}/admin/translations/${id}/`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to delete translation")
    },

    // Site Settings
    getSiteSettings: async (): Promise<SiteSettings> => {
        const response = await fetch(`${API_BASE_URL}/admin/site-settings/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch site settings")
        return response.json()
    },

    updateSiteSettings: async (formData: FormData): Promise<SiteSettings> => {
        const response = await fetch(`${API_BASE_URL}/admin/site-settings/`, {
            method: "PUT",
            headers: getAuthHeadersMultipart(),
            body: formData,
        })
        if (!response.ok) throw new Error("Failed to update site settings")
        return response.json()
    },

    // Telegram Settings
    getTelegramSettings: async (): Promise<TelegramSettings> => {
        const response = await fetch(`${API_BASE_URL}/admin/telegram-settings/`, {
            headers: getAuthHeaders(),
        })
        if (!response.ok) throw new Error("Failed to fetch telegram settings")
        return response.json()
    },

    updateTelegramSettings: async (data: TelegramSettings): Promise<TelegramSettings> => {
        const response = await fetch(`${API_BASE_URL}/admin/telegram-settings/`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        if (!response.ok) throw new Error("Failed to update telegram settings")
        return response.json()
    },

    testTelegramConnection: async (data: { bot_token: string; channel_id: string }): Promise<{ success: boolean; message: string }> => {
        const response = await fetch(`${API_BASE_URL}/admin/telegram-test/`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        return response.json()
    },
}
